import React, { useMemo, useState, useEffect } from "react";
import ReusableTable from "../components/ReusableTable";
import EmptyState from "../components/EmptyState";
import { AddPRN } from "../components/modals/AddPRN";
import { useParams, useSearchParams } from "react-router-dom";
import Spinner from "../components/PageLoader";
import { getServiceUserPRNs, deletePRN } from "../services/users";
import { WarningModal } from "../components/modals/WarningModal";
import { DeleteIcon } from "../components/icons/DeleteIcon.jsx";
import toast from "react-hot-toast";

export const PRN = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openPRNModal, setOpenPRNModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prn, setPrn] = useState([]);
  const [warningMessage, setWarningMessage] = useState("");
  const [openWarning, setOpenWarning] = useState(false);
  const [selectedPrnIndex, setSelectedPrnIndex] = useState(null);
  const urlParam = useParams();

  const onDelete = (index) => {
    setSelectedPrnIndex(index);
    setWarningMessage("Are you sure you want to delete this PRN medication?");
    setOpenWarning(true);
  };

  const warningAction = async () => {
    setIsLoading(true);
    try {
      const prnToDelete = prn[selectedPrnIndex];

      // Call the actual backend API
      await deletePRN(urlParam.id, prnToDelete.id);

      // Update local state after successful deletion
      const updatedPrn = prn.filter((_, i) => i !== selectedPrnIndex);
      setPrn(updatedPrn);
      setSelectedPrnIndex(null);
      toast.success("PRN medication deleted successfully");
      setOpenWarning(false);
    } catch (error) {
      console.error("Error deleting PRN medication:", error);
      toast.error("Failed to delete PRN medication");
      setOpenWarning(false);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "instructions",
        header: "Instructions",
      },
      {
        accessorKey: "dose",
        header: "Dosage",
      },
      {
        accessorKey: "form",
        header: "Form",
      },
      {
        accessorKey: "adminType",
        header: "Admin Type",
      },
      {
        accessorKey: "quantity",
        header: "Quantity",
      },
      {
        accessorKey: "route",
        header: "Route",
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="table-actions">
            <button
              className="action-btn action-btn--delete"
              onClick={() => onDelete(row.index)}
              data-tooltip="Delete PRN medication"
            >
              <DeleteIcon />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const pageIndex = useMemo(() => {
    const pageParam = searchParams.get("page");
    const page = parseInt(pageParam, 10);
    return isNaN(page) ? 0 : Math.max(0, page);
  }, [searchParams]);

  const updatePage = (newPageIndex) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", newPageIndex);
      return params;
    });
  };

  const fetchPRN = async () => {
    setIsLoading(true);
    try {
      const { data } = await getServiceUserPRNs(urlParam.id);
      setPrn(data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching PRN medication:", error);
      toast.error("Failed to fetch PRN medication");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPRN();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParam.id]);

  return (
    <>
      {isLoading && <Spinner />}

      {!isLoading && prn.length === 0 && (
        <EmptyState
          title="No PRN medication found"
          description="Create PRN medication to get started."
          actionButton={
            <button
              className="btn btn__primary"
              onClick={() => setOpenPRNModal(true)}
            >
              + Add PRN medication
            </button>
          }
        />
      )}
      {!isLoading && prn.length > 0 && (
        <>
          <div className="user__top">
            <div>
              <button
                className="btn btn--secondary"
                onClick={() => setOpenPRNModal(true)}
              >
                + Add PRN medication
              </button>
            </div>
          </div>
          <ReusableTable
            data={prn}
            columns={columns}
            isLoading={isLoading}
            pageIndex={pageIndex}
            emptyText="No PRN medication found"
            onPageChange={updatePage}
          />
        </>
      )}

      {openPRNModal && (
        <AddPRN
          isOpen={openPRNModal}
          onClose={() => setOpenPRNModal(false)}
          reload={fetchPRN}
          setPrn={setPrn}
          prn={prn}
        />
      )}

      {openWarning && (
        <WarningModal
          isOpen={openWarning}
          onCancel={() => setOpenWarning(false)}
          title="Warning"
          isLoading={isLoading}
          message={warningMessage}
          onConfirm={warningAction}
        />
      )}
    </>
  );
};
